import { Button, Col, Row, Select, Tooltip } from 'antd';
import type { TableProps } from 'antd';
import { useContext, useEffect, useMemo, useState } from 'react';
import { deleteProject, getAllProjects, setCurrentProject, cloneProject } from '../../features/projects/projectSlice';
import { useDispatch, useSelector } from 'react-redux';
import { NotificationContext } from '../../providers/NotificationProvider';
import {
  CopyOutlined,
  DeleteOutlined,
  EyeOutlined,
  FileDoneOutlined,
  FileExcelOutlined,
  ForkOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { constants } from '../../constants/constants';
import DataTable from '../shared/DataTable';
import { ProjectType, UserType } from '../../types/types';
import { getAllUsers } from '../../features/user/userSlice';
import {
  downloadProjectActionsReport,
  downloadProjectNodesReport,
  downloadProjectFullReport,
} from '../../services/reportService';
import { useTranslation } from 'react-i18next';
const Projects: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { triggerNotification } = useContext(NotificationContext);
  const { projects } = useSelector((store: any) => store.project);
  const { rol, user } = useSelector((store: any) => store.user);
  const users = useSelector((store: any) => store.user.users) as UserType[];
  const [pagination, setPagination] = useState<{ current: number; pageSize: number }>({
    current: 1,
    pageSize: 10,
  });
  const [scopeSelection, setScopeSelection] = useState<string | null>(null);

  const isAdminScope = rol === constants.ROL_ADMIN || user?.is_staff;

  useEffect(() => {
    if (!isAdminScope || users.length) {
      return;
    }
    dispatch(getAllUsers() as any).catch(() => {
      triggerNotification('error', t('Error'), t('Error al cargar usuarios para el filtro'), 'bottomRight');
    });
  }, [dispatch, isAdminScope, users.length, triggerNotification]);
  useEffect(() => {
    dispatch(getAllProjects() as any)
      .then((result: any) => {
        if (getAllProjects.fulfilled.match(result)) {
          triggerNotification('success', t('Éxito'), t('Se cargaron los proyectos correctamente'), 'bottomRight');
        } else {
          triggerNotification('error', t('Error'), t('Error al cargar los proyectos'), 'bottomRight');
        }
      })
      .catch((err: any) => {
        triggerNotification('error', t('Error'), t('Error al cargar los proyectos'), 'bottomRight');
      });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, [scopeSelection]);

  const normalizeValue = (value: string | null | undefined) => (value ?? '').toString().trim().toLowerCase();

  type ScopeOption = {
    value: string;
    label: string;
    type: 'company' | 'user';
    id: number;
    matchNames: string[];
  };

  const computedScopeOptions: ScopeOption[] = useMemo(() => {
    if (!isAdminScope) {
      return [];
    }
    return users
      .filter((entry: UserType) => !entry?.groups?.includes(3))
      .reduce((acc, entry) => {
        if (!entry?.id) {
          return acc;
        }
        const isCompany = entry?.groups?.includes(2);
        const firstName = normalizeValue(entry.first_name) && entry.first_name !== 'N/A' ? entry.first_name : '';
        const lastName = normalizeValue(entry.last_name) && entry.last_name !== 'N/A' ? entry.last_name : '';
        const fullName = `${firstName} ${lastName}`.trim();
        const username = entry.username || '';
        const companyName = entry.company_name && entry.company_name !== 'N/A' ? entry.company_name : '';
        const displayName = isCompany
          ? companyName || username || t('Empresa {{id}}', { id: entry.id })
          : fullName || username || entry.email || t('Usuario {{id}}', { id: entry.id });
        const option: ScopeOption = {
          value: `${isCompany ? 'company' : 'user'}:${entry.id}`,
          label: `${isCompany ? t('Empresa') : t('Usuario')}: ${displayName}`,
          type: isCompany ? 'company' : 'user',
          id: entry.id,
          matchNames: [companyName, username, fullName].filter(Boolean),
        };
        acc.push(option);
        return acc;
      }, [] as ScopeOption[])
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [isAdminScope, users]);

  const scopeOptionMap = useMemo(
    () =>
      computedScopeOptions.reduce<Record<string, ScopeOption>>((acc, option) => {
        acc[option.value] = option;
        return acc;
      }, {}),
    [computedScopeOptions]
  );

  const filteredProjects = !scopeSelection
    ? projects
    : projects.filter((project: ProjectType) => {
        const option = scopeOptionMap[scopeSelection];
        if (!option) {
          return true;
        }
        if (option.type === 'company') {
          const matchesCompanyId = project.company?.id === option.id;
          const customerName = normalizeValue(project.customer);
          const matchesCustomer = option.matchNames.some((name) => normalizeValue(name) === customerName);
          return matchesCompanyId || (customerName && matchesCustomer);
        }
        if (option.type === 'user') {
          return project.created_by?.id === option.id;
        }
        return true;
      });

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: t('Nombre'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('Metodología'),
      dataIndex: 'methodology',
      key: 'methodology',
      render: (methodology: any, record: ProjectType) =>
        record.methodology_display_name || (methodology && typeof methodology === 'object' ? methodology.name : '-'),
    },
    {
      title: t('Empresa'),
      dataIndex: 'customer',
      key: 'customer',
    },
    {
      title: t('Unidad de Negocio'),
      dataIndex: 'business_unit',
      key: 'business_unit',
    },
    {
      title: t('Creado por'),
      dataIndex: 'created_by',
      key: 'created_by',
      render: (created_by: UserType | null) => created_by?.username || '-',
    },
    {
      title: t('Fecha de Creación'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (created_at: string) => new Date(created_at).toLocaleString(),
    },
    {
      title: t('Acciones'),
      key: 'actions',
      render: (record: any) => (
        <div key={record.id}>
          <button
            type='button'
            className='naked'
            onClick={() => {
              handleView(record);
            }}
          >
            <EyeOutlined />
          </button>
          <span className='ant-divider' />
          <button
            type='button'
            className='naked'
            onClick={() => {
              handleDelete(record);
            }}
          >
            <DeleteOutlined />
          </button>
          <span className='ant-divider' />
          <button
            type='button'
            className='naked'
            onClick={() => {
              handleClone(record);
            }}
          >
            <CopyOutlined />
          </button>
          <span className='ant-divider' />
          <Tooltip title={t('Exportar Lista de Recomendaciones')}>
            <button
              type='button'
              className='naked'
              disabled={record.can_export_actions_report === false}
              onClick={() => {
                if (record.can_export_actions_report === false) {
                  return;
                }
                handleDownloadReport(record);
              }}
            >
              <FileExcelOutlined />
            </button>
          </Tooltip>
          <span className='ant-divider' />
          <Tooltip title={t('Exportar Lista de Nodos')}>
            <button
              type='button'
              className='naked'
              onClick={() => {
                handleDownloadNodesReport(record);
              }}
            >
              <ForkOutlined />
            </button>
          </Tooltip>
          <span className='ant-divider' />
          <Tooltip title={t('Exportar Proyecto Completo')}>
            <button
              type='button'
              className='naked'
              onClick={() => {
                handleDownloadFullReport(record);
              }}
            >
              <FileDoneOutlined />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];

  const handleView = (item: any) => {
    dispatch(setCurrentProject(item));
    navigate(`/proyecto/${item.id}/ver`);
  };

  const handleDelete = (item: any) => {
    if (window.confirm(t('¿Estás seguro de eliminar este proyecto?'))) {
      dispatch(deleteProject(item.id) as any)
        .then((result: any) => {
          if (deleteProject.fulfilled.match(result)) {
            triggerNotification('success', t('Éxito'), t('Proyecto eliminado correctamente'), 'bottomRight');
            dispatch(getAllProjects() as any);
          } else {
            triggerNotification('error', t('Error'), t('Error al eliminar el proyecto'), 'bottomRight');
          }
        })
        .catch((err: any) => {
          triggerNotification('error', t('Error'), t('Error al eliminar el proyecto'), 'bottomRight');
        });
    }
  };

  const handleClone = (item: ProjectType) => {
    if (!item.id) {
      triggerNotification('error', t('Error'), t('Proyecto inválido para clonar'), 'bottomRight');
      return;
    }

    dispatch(cloneProject(item.id) as any)
      .then((result: any) => {
        if (cloneProject.fulfilled.match(result)) {
          const clonedProject = result.payload as ProjectType | undefined;
          triggerNotification('success', t('Éxito'), t('Proyecto clonado correctamente'), 'bottomRight');
          dispatch(getAllProjects() as any)
            .then((fetchResult: any) => {
              if (getAllProjects.fulfilled.match(fetchResult)) {
                const refreshedProjects = fetchResult.payload as ProjectType[];
                const targetId = clonedProject?.id;
                if (targetId) {
                  const newIndex = refreshedProjects.findIndex((project) => project.id === targetId);
                  if (newIndex !== -1) {
                    setPagination((prev) => {
                      const size = prev.pageSize || 10;
                      const page = Math.floor(newIndex / size) + 1;
                      return { ...prev, current: page };
                    });
                  }
                }
              } else {
                triggerNotification('error', t('Error'), t('Error al recargar los proyectos'), 'bottomRight');
              }
            })
            .catch(() => {
              triggerNotification('error', t('Error'), t('Error al recargar los proyectos'), 'bottomRight');
            });
        } else {
          triggerNotification('error', t('Error'), t('Error al clonar el proyecto'), 'bottomRight');
        }
      })
      .catch(() => {
        triggerNotification('error', t('Error'), t('Error al clonar el proyecto'), 'bottomRight');
      });
  };

  const handleDownloadReport = async (item: ProjectType) => {
    if (!item.id) {
      triggerNotification('error', t('Error'), t('Proyecto inválido para exportar'), 'bottomRight');
      return;
    }

    try {
      await downloadProjectActionsReport(item.id, item.name);
      triggerNotification('success', t('Éxito'), t('Descarga del reporte iniciada'), 'bottomRight');
    } catch (error) {
      triggerNotification('error', t('Error'), t('Error al descargar el reporte'), 'bottomRight');
    }
  };

  const handleDownloadNodesReport = async (item: ProjectType) => {
    if (!item.id) {
      triggerNotification('error', t('Error'), t('Proyecto inválido para exportar'), 'bottomRight');
      return;
    }

    try {
      await downloadProjectNodesReport(item.id, item.name);
      triggerNotification('success', t('Éxito'), t('Descarga del reporte de nodos iniciada'), 'bottomRight');
    } catch (error) {
      triggerNotification('error', t('Error'), t('Error al descargar el reporte de nodos'), 'bottomRight');
    }
  };

  const handleDownloadFullReport = async (item: ProjectType) => {
    if (!item.id) {
      triggerNotification('error', t('Error'), t('Proyecto inválido para exportar'), 'bottomRight');
      return;
    }

    try {
      await downloadProjectFullReport(item.id, item.name);
      triggerNotification('success', t('Éxito'), t('Descarga del proyecto completo iniciada'), 'bottomRight');
    } catch (error) {
      triggerNotification('error', t('Error'), t('Error al descargar el proyecto completo'), 'bottomRight');
    }
  };

  const handleTableChange: TableProps<ProjectType>['onChange'] = (newPagination, _filters, _sorter, _extra) => {
    if (!newPagination) {
      return;
    }
    setPagination((prev) => ({
      current: newPagination.current ?? prev.current,
      pageSize: newPagination.pageSize ?? prev.pageSize,
    }));
  };

  const filteredColumns = columns.filter((col) => {
    if ((rol === constants.ROL_EMPRESA || rol === constants.ROL_USER) && col.key === 'company') {
      return false; // Remueve la columna 'Nombre de Empresa'
    }
    if (rol === constants.ROL_INDEPENDIENTE && (col.key === 'company' || col.key === 'user')) {
      return false; // Remueve la columna 'Nombre de Empresa'
    }
    return true; // Mantiene las demás columnas
  });
  return (
    <Row className='content'>
      <Col style={{ width: '100%' }}>
        <div className='title-full'>
          <h1>{t('Proyectos')}</h1>
          <Button
            type='primary'
            onClick={
              () => navigate('/proyecto/nuevo') // '/usuario/nuevo' es la ruta de creación de usuarios en el backend
            }
          >
            {t('Nuevo Proyecto')}
          </Button>
        </div>
        <div className='ant-divider'></div>
        {isAdminScope && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 12 }}>
            <span>{t('Ver por:')}</span>
            <Select
              style={{ minWidth: 380 }}
              placeholder={t('Empresa o usuario')}
              allowClear
              value={scopeSelection ?? undefined}
              onChange={(value) => setScopeSelection(value ?? null)}
              showSearch
              optionFilterProp='label'
              filterOption={(input, option) => {
                const normalizedInput = normalizeValue(input);
                const label = typeof option?.label === 'string' ? option.label : '';
                return normalizeValue(label).includes(normalizedInput);
              }}
              options={computedScopeOptions.map((option) => ({ value: option.value, label: option.label }))}
            />
          </div>
        )}
        <DataTable
          dataSource={filteredProjects}
          columns={filteredColumns}
          pagination={{ ...pagination } as TableProps<ProjectType>['pagination']}
          onChange={handleTableChange}
        />
      </Col>
    </Row>
  );
};

export default Projects;
